import * as React from 'react'
import { VariableSizeList, VariableSizeList as VariableSizeListType, ListChildComponentProps } from 'react-window'

/**
 * Virtualized listbox for the GapAutocomplete primitives. Renders only the rows in view
 * so dropdowns with thousands of options open in one frame.
 *
 * Wired via `slotProps.listbox.component={GapAutocompleteListbox}` (NOT
 * `slots.listbox`). That distinction matters: `slots.listbox` *replaces* MUI's styled
 * `<ul>` and loses the descendant `& .MuiAutocomplete-option` rules that give each row
 * its hover, focused, selected, padding, and min-height styling.
 * `slotProps.listbox.component` keeps the styled wrapper and uses this component as its
 * rendered root via emotion's `as`, so all those styles still cascade onto the rows.
 *
 * Row heights are derived from the `data-option-type` attribute set by
 * `gapAutoCompleteRenderOption`, so the list can size itself without measuring the DOM.
 *
 * We must override two of the styled wrapper's rules (`max-height: 40vh` and
 * `overflow: auto`) on the outer element so react-window's internal scroll container
 * is the one that actually scrolls — otherwise the wrapper clips the virtualized list.
 */

// Heights match the natural row size at the `sm` breakpoint and up, where MUI's
// `.MuiAutocomplete-option` min-height of 48 relaxes to `auto`. Label content (~24px)
// + 8px of our sx paddingY gives a ~40px row. Setting these any larger makes the row's
// flex centering distribute slack above and below the content, which reads as padding.
const ITEM_HEIGHTS: Record<string, number> = {
   category: 48,
   subcategory: 36,
   'option-with-sublabel': 56,
   option: 40,
   special: 40,
}
const DEFAULT_ITEM_HEIGHT = 40
const MAX_VISIBLE_ROWS = 8
const OVERSCAN_COUNT = 8

type AutocompleteChild = React.ReactElement<{ 'data-option-type'?: string; style?: React.CSSProperties }>

const getItemHeight = (item: AutocompleteChild) => {
   const type = item.props['data-option-type']
   return (type && ITEM_HEIGHTS[type]) || DEFAULT_ITEM_HEIGHT
}

const OuterElementContext = React.createContext<React.HTMLAttributes<HTMLElement>>({})

const OuterElement = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(function OuterElement(props, ref) {
   const outerProps = React.useContext(OuterElementContext)
   return <div ref={ref} {...props} {...outerProps} />
})

// Inner element is a <ul> with browser defaults zeroed so the virtualized <li> rows sit
// in their natural semantic container without inheriting browser padding/margins.
const InnerElement = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(function InnerElement(props, ref) {
   return <ul ref={ref} {...props} style={{ ...props.style, margin: 0, padding: 0, listStyle: 'none' }} />
})

const Row = ({ data, index, style }: ListChildComponentProps<AutocompleteChild[]>) => {
   const item = data[index]
   const inlineStyle: React.CSSProperties = { ...style, ...item.props.style }
   return React.cloneElement(item, { style: inlineStyle })
}

const useResetCache = (itemCount: number) => {
   const ref = React.useRef<VariableSizeListType<AutocompleteChild[]> | null>(null)
   React.useEffect(() => {
      if (ref.current) ref.current.resetAfterIndex(0, true)
   }, [itemCount])
   return ref
}

const GapAutocompleteListbox = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
   function GapAutocompleteListbox(props, ref) {
      // The styled `AutocompleteListbox` wrapper passes its emotion className + standard
      // listbox attrs in `props`. The className must land on this outer div so the
      // descendant `& .MuiAutocomplete-option` rules find our rows.
      const { children, className, style, ...listboxProps } = props
      const items = React.Children.toArray(children) as AutocompleteChild[]
      const listRef = useResetCache(items.length)

      const heights = items.map(getItemHeight)
      const totalHeight = heights.reduce((sum, h) => sum + h, 0)
      const maxHeightBudget = MAX_VISIBLE_ROWS * DEFAULT_ITEM_HEIGHT
      const listHeight = items.length > MAX_VISIBLE_ROWS ? maxHeightBudget : totalHeight

      return (
         <div
            ref={ref}
            className={className}
            style={{
               ...style,
               // Disarm the styled wrapper's own scroll so react-window's inner scroll
               // container is the one that actually scrolls. The wrapper still applies
               // its `padding: 8px 0` (the gap above the first row and below the last),
               // its `position: relative`, and crucially the descendant
               // `& .MuiAutocomplete-option` rules that style every <li>.
               maxHeight: 'none',
               overflow: 'visible',
            }}>
            <OuterElementContext.Provider value={listboxProps}>
               <VariableSizeList
                  itemData={items}
                  height={listHeight}
                  itemCount={items.length}
                  itemSize={index => heights[index]}
                  width="100%"
                  ref={listRef}
                  outerElementType={OuterElement}
                  innerElementType={InnerElement}
                  overscanCount={OVERSCAN_COUNT}>
                  {Row}
               </VariableSizeList>
            </OuterElementContext.Provider>
         </div>
      )
   }
)

export default GapAutocompleteListbox
